import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams, Navigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Icon } from '@/components/ui/Icon';
import { RetryImage } from '@/components/ui';
import { ICONS } from '@/constants/icons';
import { channelsService } from '@/services/channels.service';
import { formatApiError } from '@/services/api';
import { toast } from '@/utils/toast';
import { cn } from '@/utils/cn';
import { useAuth } from '@/hooks/useAuth';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { teamQueryKeys } from '@/lib/queryKeys';
import { ConnectionErrorScreen } from '@/components/ConnectionErrorScreen';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin, faFacebook, faInstagram, faYoutube, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { devLog } from '@/utils/logger';

/**
 * Generic Channel Selection Page
 *
 * After OAuth redirect, users land here to select which channels to connect.
 * Works for all platforms: LinkedIn, Facebook, Google Business, YouTube
 *
 * URL: /channels/:platform/select?session={sessionId}
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface PlatformConfig {
  name: string;
  displayName: string;
  icon: IconDefinition | string;
  primaryColor: string;
  accentColor: string;
  entityLabels: {
    profile: string;
    entities: string;
    singular: string;
  };
  hasProfile: boolean;
}

interface ProfileData {
  id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  profilePicture?: string;
  email?: string;
}

interface EntityData {
  id: string;
  name: string;
  vanityName?: string;
  logo?: string;
  picture?: string; // Facebook returns "picture" instead of "logo"
  urn?: string;
  metadata?: Record<string, unknown>;
  instagramAccount?: {
    id: string;
    username: string;
    picture?: string;
  };
}

interface PendingConnectionData {
  profile?: ProfileData;
  entities: EntityData[];
}

// ============================================================================
// PLATFORM CONFIGURATION
// ============================================================================

const getPlatformConfig = (t: TFunction): Record<string, PlatformConfig> => ({
  linkedin: {
    name: 'linkedin',
    displayName: 'LinkedIn',
    icon: faLinkedin,
    primaryColor: '#0A66C2',
    accentColor: '#0073B1',
    entityLabels: {
      profile: t('channel_select_linkedin_profile'),
      entities: t('channel_select_linkedin_entities'),
      singular: t('channel_select_linkedin_singular'),
    },
    hasProfile: true,
  },
  facebook: {
    name: 'facebook',
    displayName: 'Facebook',
    icon: faFacebook,
    primaryColor: '#1877F2',
    accentColor: '#0866FF',
    entityLabels: {
      profile: t('channel_select_facebook_profile'),
      entities: t('channel_select_facebook_entities'),
      singular: t('channel_select_facebook_singular'),
    },
    hasProfile: false,
  },
  'google-my-business': {
    name: 'google-my-business',
    displayName: 'Google Business',
    icon: faGoogle,
    primaryColor: '#4285F4',
    accentColor: '#1967D2',
    entityLabels: {
      profile: t('channel_select_google_profile'),
      entities: t('channel_select_google_entities'),
      singular: t('channel_select_google_singular'),
    },
    hasProfile: false,
  },
  youtube: {
    name: 'youtube',
    displayName: 'YouTube',
    icon: faYoutube,
    primaryColor: '#FF0000',
    accentColor: '#CC0000',
    entityLabels: {
      profile: t('channel_select_youtube_profile'),
      entities: t('channel_select_youtube_entities'),
      singular: t('channel_select_youtube_singular'),
    },
    hasProfile: false,
  },
});

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get profile display name (handles firstName+lastName or single name field)
 */
const getProfileName = (profile: ProfileData | undefined, fallback: string): string => {
  if (!profile) return fallback;
  // Try firstName + lastName first
  if (profile.firstName || profile.lastName) {
    return `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || fallback;
  }
  // Fall back to name field
  return profile.name || fallback;
};

/**
 * Get initials from profile name for avatar fallback
 */
const getProfileInitials = (profile: ProfileData | undefined, fallback: string): string => {
  const name = getProfileName(profile, fallback);
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name[0]?.toUpperCase() || '?';
};

/**
 * Format vanity name as URL (LinkedIn-specific, but kept for flexibility)
 */
const formatVanityUrl = (platform: string, vanityName?: string): string | null => {
  if (!vanityName) return null;

  switch (platform) {
    case 'linkedin':
      return `linkedin.com/company/${vanityName}`;
    case 'facebook':
      return `facebook.com/${vanityName}`;
    default:
      return vanityName;
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ChannelSelectPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { platform } = useParams<{ platform: string }>();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const { t } = useTranslation('common');

  // Get auth state to prevent race condition with OAuth redirect
  const { isAuthenticated, isLoading: isAuthLoading, authConnectionError } = useAuth();
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  // Get platform configuration
  const PLATFORM_CONFIG = getPlatformConfig(t);
  const config = PLATFORM_CONFIG[platform || ''];

  // ============================================================================
  // ALL HOOKS MUST BE DEFINED BEFORE ANY EARLY RETURNS (React Rules of Hooks)
  // ============================================================================

  // Selection state - pre-select profile only if platform supports it
  const [selectedItems, setSelectedItems] = useState<Set<string>>(
    () => new Set(config?.hasProfile ? ['profile'] : [])
  );

  // State to track which entities have Instagram included
  const [includeInstagram, setIncludeInstagram] = useState<Set<string>>(() => new Set());

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const {
    data,
    isLoading,
    error,
  } = useQuery<PendingConnectionData>({
    queryKey: [platform, 'pending', sessionId],
    queryFn: () => channelsService.getPendingConnection(platform!, sessionId!),
    retry: false, // Don't retry if session expired
    enabled: !!config && !!sessionId && !!platform && isAuthenticated && !isAuthLoading, // Wait for auth to be ready
  });

  // ============================================================================
  // MUTATIONS
  // ============================================================================

  const connectMutation = useMutation({
    mutationFn: (selectedChannels: Array<{ type: string; id?: string }>) =>
      channelsService.connectChannels(platform!, {
        sessionId: sessionId!,
        selectedChannels,
      }),
    onSuccess: (result) => {
      try {
        devLog.log('Connect success, result:', result);
        const count = result?.channels?.length ?? 0;
        // Invalidate all channel queries so PostCreator and other consumers
        // immediately see the newly connected channels without a hard refresh.
        queryClient.invalidateQueries({ queryKey: teamQueryKeys.channels.all(teamId) });
        toast.success(t('channel_select_success', { count, platform: config?.displayName ?? platform }));
        devLog.log('Navigating to /settings?tab=channels');
        navigate('/settings?tab=channels');
      } catch (err) {
        devLog.error('Error in onSuccess:', err);
        // Still navigate even if toast fails
        navigate('/settings?tab=channels');
      }
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const totalItems = useMemo(() => {
    if (!data || !config) return 0;
    const profileCount = config.hasProfile && data.profile ? 1 : 0;
    return profileCount + (data.entities?.length || 0);
  }, [data, config]);

  const isAllSelected = useMemo(() => {
    if (!data || !config || totalItems === 0) return false;
    const profileIds = config.hasProfile && data.profile ? ['profile'] : [];
    const allIds = [...profileIds, ...(data.entities || []).map((entity) => String(entity.id))];
    return allIds.every(id => selectedItems.has(id));
  }, [data, selectedItems, config, totalItems]);

  const canConnect = selectedItems.size > 0 && !connectMutation.isPending;

  // ============================================================================
  // EVENT HANDLERS (defined after hooks, before early returns)
  // ============================================================================

  const toggleSelection = (id: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (!data || !config) return;

    const profileIds = config.hasProfile && data.profile ? ['profile'] : [];
    const allIds = [...profileIds, ...(data.entities || []).map((entity) => String(entity.id))];

    // Check if currently all selected using .every()
    const isCurrentlyAllSelected = allIds.every(id => selectedItems.has(id));

    if (isCurrentlyAllSelected) {
      // Deselect all except profile (if platform has profile)
      setSelectedItems(new Set(config.hasProfile ? ['profile'] : []));
      setIncludeInstagram(new Set());
    } else {
      // Select all
      setSelectedItems(new Set(allIds));
      // Also include all Instagram accounts
      const igIds = (data.entities || [])
        .filter(entity => entity.instagramAccount)
        .map(entity => String(entity.id));
      setIncludeInstagram(new Set(igIds));
    }
  };

  const handleConnect = () => {
    // Map generic "entity" to platform-specific type
    // LinkedIn uses "organization", Facebook uses "page", etc.
    const entityTypeMap: Record<string, string> = {
      linkedin: 'organization',
      facebook: 'page',
      'google-my-business': 'location',
      youtube: 'channel',
    };
    const entityType = entityTypeMap[platform!] || 'entity';

    const channels = Array.from(selectedItems).map((id) => {
      if (id === 'profile') {
        return { type: "profile" as const, id: undefined };
      }
      return {
        type: entityType as "profile" | "organization" | "page" | "location" | "channel",
        id,
        includeInstagram: includeInstagram.has(id)
      };
    });

    connectMutation.mutate(channels);
  };

  const handleCancel = () => {
    navigate('/settings?tab=channels');
  };

  // ============================================================================
  // EARLY RETURNS (MUST come AFTER all hooks are defined)
  // ============================================================================

  // Show loading while auth is initializing
  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-500">{t('channel_select_initializing')}</p>
        </div>
      </div>
    );
  }

  // Transient connectivity failure during the boot-time auth check — the
  // session may still be valid, so do NOT redirect to /login. Let the user
  // retry instead of silently discarding a valid session.
  if (authConnectionError && !isAuthenticated) {
    return <ConnectionErrorScreen />;
  }

  // Redirect to login if not authenticated after auth loading completes
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect if invalid platform
  if (!config) {
    return <Navigate to="/settings?tab=channels" replace />;
  }

  // ============================================================================
  // ERROR STATES
  // ============================================================================

  if (!sessionId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-red-500 to-orange-500" />
            <div className="p-8 text-center">
              <div className="mx-auto mb-6 h-16 w-16 rounded-2xl bg-red-100 flex items-center justify-center">
                <Icon name={ICONS.WARNING} size="xl" color="text-red-600" ariaHidden />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('channel_select_invalid_session_title')}</h2>
              <p className="text-gray-600 mb-6">
                {t('channel_select_invalid_session_message', { platform: config.displayName })}
              </p>
              <Button onClick={() => navigate('/settings?tab=channels')} variant="primary">
                {t('channel_select_go_to_settings')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const isSessionExpired = error instanceof Error && error.message.includes('404');

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-red-500 to-orange-500" />
            <div className="p-8 text-center">
              <div className="mx-auto mb-6 h-16 w-16 rounded-2xl bg-red-100 flex items-center justify-center">
                <Icon name={ICONS.WARNING} size="xl" color="text-red-600" ariaHidden />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                {isSessionExpired ? t('channel_select_session_expired_title') : t('channel_select_connection_failed_title')}
              </h2>
              <p className="text-gray-600 mb-6">
                {isSessionExpired
                  ? t('channel_select_session_expired_message', { platform: config.displayName })
                  : t('channel_select_connection_failed_message', { platform: config.displayName })}
              </p>
              <Button onClick={() => navigate('/settings?tab=channels')} variant="primary">
                {t('channel_select_go_to_settings')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // LOADING STATE
  // ============================================================================

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div
            className="mx-auto mb-4 h-16 w-16 rounded-2xl flex items-center justify-center animate-pulse"
            style={{
              background: `linear-gradient(to bottom right, ${config.primaryColor}, ${config.accentColor})`,
            }}
          >
            {typeof config.icon === 'string' ? (
              <Icon name={config.icon} size="xl" color="text-white" ariaHidden />
            ) : (
              <FontAwesomeIcon icon={config.icon} className="text-white text-4xl" />
            )}
          </div>
          <p className="text-lg font-medium text-gray-900">{t('channel_select_loading', { platform: config.displayName })}</p>
          <p className="text-sm text-gray-500 mt-2">{t('channel_select_loading_subtitle')}</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // MAIN UI
  // ============================================================================

  if (!data) {
    return null;
  }

  const { profile, entities: rawEntities = [] } = data;
  // Ensure entity IDs are strings for Set operations
  const entities = rawEntities.map(e => ({ ...e, id: String(e.id) }));
  const unknown = t('channel_select_unknown');
  const profileName = getProfileName(profile, unknown);

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Page Header */}
      <PageHeader
        title={t('channel_select_title', { platform: config.displayName })}
        subtitle={t('channel_select_subtitle', { platform: config.displayName, entities: config.entityLabels.entities.toLowerCase() })}
        headerType="dashboard"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8 py-8">

        {/* Selection Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name={ICONS.INFO} size="sm" color="text-gray-500" ariaHidden />
            <span className="text-sm text-gray-600">
              {t('channel_select_count', { count: selectedItems.size, selected: selectedItems.size, total: totalItems })}
            </span>
          </div>
          {totalItems > 1 && (
            <button
              onClick={toggleAll}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              {isAllSelected ? t('channel_select_deselect_all') : t('channel_select_select_all')}
            </button>
          )}
        </div>

        {/* Personal Profile Card (if platform supports it) */}
        {config.hasProfile && profile && (
          <div
            className={cn(
              'relative overflow-hidden rounded-2xl border-2 bg-white shadow-sm transition-all duration-200 cursor-pointer hover:shadow-md',
              selectedItems.has('profile')
                ? 'border-primary-500 ring-2 ring-primary-100'
                : 'border-gray-200 hover:border-gray-300'
            )}
            onClick={() => toggleSelection('profile')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleSelection('profile');
              }
            }}
          >
            <div
              className="absolute inset-x-0 top-0 h-1"
              style={{
                background: `linear-gradient(to right, ${config.primaryColor}, ${config.accentColor})`,
              }}
            />
            <div className="p-6">
              <div className="flex items-start gap-4">
                {/* Checkbox */}
                <div className="flex-shrink-0 pt-1" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedItems.has('profile')}
                    onCheckedChange={() => toggleSelection('profile')}
                  />
                </div>

                {/* Profile Picture */}
                <div className="flex-shrink-0">
                  {profile.profilePicture ? (
                    <RetryImage
                      src={profile.profilePicture}
                      alt={profileName}
                      className="h-16 w-16 rounded-xl object-cover border-2 border-gray-100"
                    />
                  ) : (
                    <div
                      className="h-16 w-16 rounded-xl flex items-center justify-center text-white text-xl font-bold border-2 border-gray-100"
                      style={{
                        background: `linear-gradient(to bottom right, ${config.primaryColor}, ${config.accentColor})`,
                      }}
                    >
                      {getProfileInitials(profile, unknown)}
                    </div>
                  )}
                </div>

                {/* Profile Info - Flexible container */}
                <div className="flex-grow">
                  <div className="flex items-start gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900 flex-grow">
                      {profileName}
                    </h3>
                    <span
                      className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap"
                      style={{
                        backgroundColor: `${config.primaryColor}10`,
                        color: config.primaryColor,
                        borderColor: `${config.primaryColor}30`,
                      }}
                    >
                      <Icon name={ICONS.USER} size="xs" ariaHidden />
                      {config.entityLabels.profile}
                    </span>
                  </div>
                  {profile.email && (
                    <p className="text-sm text-gray-600">{profile.email}</p>
                  )}
                </div>

                {/* Platform Icon */}
                <div className="flex-shrink-0 hidden sm:block">
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center"
                    style={{
                      backgroundColor: `${config.primaryColor}10`,
                    }}
                  >
                    {typeof config.icon === 'string' ? (
                      <Icon
                        name={config.icon}
                        size="md"
                        style={{ color: config.primaryColor }}
                        ariaHidden
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={config.icon}
                        style={{ color: config.primaryColor }}
                        className="text-xl"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Entities Section */}
        {entities.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {typeof config.icon === 'string' ? (
                <Icon name={config.icon} size="sm" color="text-gray-700" ariaHidden />
              ) : (
                <FontAwesomeIcon
                  icon={config.icon}
                  style={{ color: config.primaryColor }}
                  className="text-lg"
                />
              )}
              <h2 className="text-lg font-semibold text-gray-900">{config.entityLabels.entities}</h2>
              <span className="text-sm text-gray-500">({entities.length})</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {entities.map((entity) => (
                <div
                  key={entity.id}
                  className={cn(
                    'relative overflow-hidden rounded-2xl border-2 bg-white shadow-sm transition-all duration-200 cursor-pointer hover:shadow-md',
                    selectedItems.has(entity.id)
                      ? 'border-primary-500 ring-2 ring-primary-100'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                  onClick={() => toggleSelection(entity.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleSelection(entity.id);
                    }
                  }}
                >
                  <div
                    className="absolute inset-x-0 top-0 h-1"
                    style={{
                      background: `linear-gradient(to right, ${config.accentColor}, ${config.primaryColor})`,
                    }}
                  />
                  <div className="p-5">
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <div className="flex-shrink-0 pt-1" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedItems.has(entity.id)}
                          onCheckedChange={() => toggleSelection(entity.id)}
                        />
                      </div>

                      {/* Entity Logo */}
                      <div className="flex-shrink-0">
                        {(entity.logo || entity.picture) ? (
                          <RetryImage
                            src={entity.logo || entity.picture}
                            alt={entity.name}
                            className="h-12 w-12 rounded-lg object-cover border-2 border-gray-100"
                          />
                        ) : (
                          <div
                            className="h-12 w-12 rounded-lg flex items-center justify-center"
                            style={{
                              background: `linear-gradient(to bottom right, ${config.accentColor}, ${config.primaryColor})`,
                            }}
                          >
                            <Icon name={ICONS.USERS} size="md" color="text-white" ariaHidden />
                          </div>
                        )}
                      </div>

                      {/* Entity Info */}
                      <div className="flex-grow">
                        <h4 className="text-base font-semibold text-gray-900 mb-1 break-words">
                          {entity.name || t('channel_select_unknown_organization')}
                        </h4>
                        {entity.vanityName && (
                          <p className="text-xs text-gray-500 break-all">
                            {formatVanityUrl(platform!, entity.vanityName)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Instagram Account (if linked to this page) */}
                    {entity.instagramAccount && (
                      <div
                        className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-3 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIncludeInstagram(prev => {
                            const next = new Set(prev);
                            if (next.has(entity.id)) {
                              next.delete(entity.id);
                            } else {
                              next.add(entity.id);
                            }
                            return next;
                          });
                        }}
                      >
                        <div onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={includeInstagram.has(entity.id)}
                            onCheckedChange={() => {
                              setIncludeInstagram(prev => {
                                const next = new Set(prev);
                                if (next.has(entity.id)) {
                                  next.delete(entity.id);
                                } else {
                                  next.add(entity.id);
                                }
                                return next;
                              });
                            }}
                          />
                        </div>
                        <div className="flex items-center gap-2 flex-1">
                          {entity.instagramAccount.picture ? (
                            <RetryImage
                              src={entity.instagramAccount.picture}
                              alt={entity.instagramAccount.username}
                              className="h-8 w-8 rounded-full object-cover border border-gray-200"
                            />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center">
                              <Icon name={ICONS.USER} size="sm" color="text-white" ariaHidden />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              @{entity.instagramAccount.username}
                            </p>
                            <p className="text-xs text-gray-500">{t('common:channel_select_include_instagram')}</p>
                          </div>
                          <FontAwesomeIcon
                            icon={faInstagram}
                            className="text-lg"
                            style={{ color: '#E4405F' }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State for No Entities */}
        {entities.length === 0 && !profile && (
          <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 shadow-sm">
            <div className="p-8 text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-xl bg-gray-200 flex items-center justify-center">
                <Icon name={ICONS.INFO} size="md" color="text-gray-500" ariaHidden />
              </div>
              <p className="text-sm text-gray-600">
                {t('channel_select_no_entities', { entities: config.entityLabels.entities.toLowerCase(), singular: config.entityLabels.singular })}
              </p>
            </div>
          </div>
        )}

        {/* Empty State for No Entities (but has profile) */}
        {entities.length === 0 && profile && (
          <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 shadow-sm">
            <div className="p-8 text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-xl bg-gray-200 flex items-center justify-center">
                <Icon name={ICONS.INFO} size="md" color="text-gray-500" ariaHidden />
              </div>
              <p className="text-sm text-gray-600">
                {t('channel_select_no_entities_profile', { entities: config.entityLabels.entities.toLowerCase(), profile: config.entityLabels.profile.toLowerCase() })}
              </p>
            </div>
          </div>
        )}

        {/* Action Bar - Fixed at bottom on mobile, inline on desktop */}
        <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg sm:relative sm:border-0 sm:shadow-none sm:bg-transparent">
          <div className="p-4 sm:p-0 flex items-center justify-between gap-4">
            <Button
              onClick={handleCancel}
              variant="secondary"
              size="lg"
              disabled={connectMutation.isPending}
              className="flex-1 sm:flex-none"
            >
              {t('channel_select_cancel')}
            </Button>
            <Button
              onClick={handleConnect}
              variant="primary"
              size="lg"
              loading={connectMutation.isPending}
              disabled={!canConnect}
              className="flex-1 sm:flex-none"
            >
              {connectMutation.isPending
                ? t('channel_select_connecting')
                : t('channel_select_connect_button', { count: selectedItems.size })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
